// Dashboard Portal
// pages/Steps/SignUpSteps.js (UPDATED WITH TERMS)

import React, { useState, useEffect, useRef, useCallback } from "react";
import ProfilePage from "../UserProfile/Profile";
import CreatePrompts from "../Professions/CreatePrompts";
import PricingPage from "../Billing/PricingPage";
import TermsAcceptanceStep from "../Terms/TermsAcceptanceStep"; // NEW IMPORT
import { Stepper, Step, StepLabel, Box, Paper, Typography, Button, CircularProgress } from "@mui/material";
import { styled } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { SETUP_COMPLETE_LOCAL_KEY } from "../../config";

// --- Helper Components ---
const InlineError = ({ message }) => message ? <Typography color="error" sx={{ mt: 1, mb: 1, textAlign: 'center' }}>Error: {message}</Typography> : null;
const InlineSpinner = () => <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />;

// --- Styled Component ---
const StyledPaper = styled(Paper)(({ theme }) => ({ 
    padding: theme.spacing(3), display: 'flex', flexDirection: 'column', gap: theme.spacing(2),
    margin: '50px auto', maxWidth: '60%', minWidth: '450px',
}));

// --- Main Component ---
export default function SignUpSteps() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // State
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessingStep, setIsProcessingStep] = useState(false);
    const [profileFormValid, setProfileFormValid] = useState(false);
    const [error, setError] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const [userRole, setUserRole] = useState(null);
    const [steps, setSteps] = useState([]);

    // Refs
    const profilePageRef = useRef(null);

    // --- localStorage Key ---
    let SETUP_COMPLETE_KEY = SETUP_COMPLETE_LOCAL_KEY;

    // --- Dynamic Step Definitions based on User Role ---
    const getStepsForRole = (role) => {
        const isAdmin = role?.toLowerCase() === "admin";
        
        if (isAdmin) {
            // Admin flow: Profile → Terms → Plan
            return [
                { label: 'Profile Settings', slug: 'profile', path: '/update-profile' },
                { label: 'Accept Terms', slug: 'terms', path: '/accept-terms' },
                { label: 'Select Plan', slug: 'plan', path: '/select-plan' },
            ];
        } else {
            // Non-admin flow: Profile → Terms
            return [
                { label: 'Complete Your Profile', slug: 'profile', path: '/update-profile' },
                { label: 'Accept Terms', slug: 'terms', path: '/accept-terms' },
            ];
        }
    };

    // Helper to find step index from SLUG
    const getStepIndexFromSlug = (slug, stepsArray) => {
        const index = stepsArray.findIndex(step => step.slug === slug);
        return index === -1 ? 0 : index;
    };

    // Helper to find slug from PATH
    const getSlugFromPath = (pathname, stepsArray) => {
        const step = stepsArray.find(s => s.path === pathname);
        return step ? step.slug : null;
    };

    // Helper to determine the next required step SLUG based on role and data
    const determineRequiredStepSlug = (userData, stepsArray) => {
        if (!userData || !stepsArray.length) return null;
        
        const isAdmin = userData?.active_team?.user_role_in_team?.toLowerCase() === "admin";
        
        // Step 1: Check profile
        if (!userData.profile) {
            return stepsArray[0].slug; // Need profile (both admin and non-admin)
        }
        
        // Step 2: Check terms acceptance
        if (userData.active_team?.has_pending_terms) {
            return stepsArray[1].slug; // Need to accept terms
        }
        
        // Step 3: Check plan (admin only)
        if (isAdmin && !userData.active_team?.subscription) {
            return stepsArray[2].slug; // Admin needs plan
        }
        
        // All steps complete
        return null;
    };

    // --- Effect 1: Initial Authentication & Role Detection ---
    useEffect(() => {
        setIsLoading(true);
        setError(null);

        let userData = {};
        const userStr = localStorage.getItem('user');
        if (userStr) {
            userData = JSON.parse(userStr);
        }

        // 1. Auth Check
        if (!userData) {
            console.log("Effect 1: Not authenticated, redirecting to Login.");
            navigate('/Login', { replace: true });
            return;
        }

        // 2. Determine user role and set appropriate steps
        const role = userData?.active_team?.user_role_in_team;
        setUserRole(role);
        const roleSteps = getStepsForRole(role);
        setSteps(roleSteps);

        // 3. Check if Setup is Already Complete
        const isSetupCompleteFlag = localStorage.getItem(SETUP_COMPLETE_KEY) === 'true';
        const requiredSlugCheck = determineRequiredStepSlug(userData, roleSteps);

        if (isSetupCompleteFlag || requiredSlugCheck === null) {
            console.log("Effect 1: Setup complete, redirecting to Dashboard.");
            window.location.href = "/chatbots";
            return;
        }

        console.log("Effect 1: Auth & role checks passed. User role:", role);
        setIsLoading(false);

    }, [navigate]);

    // --- Effect 2: Enforce Required Step & Synchronize UI ---
    useEffect(() => {
        if (isLoading || !steps.length) return;

        console.log("Effect 2: Running step enforcement and UI sync.");
        setError(null);

        let userData = {};
        const userStr = localStorage.getItem('user');
        if (userStr) {
            userData = JSON.parse(userStr);
        }

        // Determine the step the user must be on
        const requiredSlug = determineRequiredStepSlug(userData, steps);
        
        if (requiredSlug === null) {
            const isSetupCompleteFlag = localStorage.getItem(SETUP_COMPLETE_KEY) === 'true';
            if(isSetupCompleteFlag || requiredSlug === null){
                console.log("Effect 2: Data indicates completion, redirecting to Dashboard.");
                window.location.href = "/chatbots";
                return;
            }
        }

        // Get the step slug currently in the URL
        const currentSlugInUrl = searchParams.get('step')?.toLowerCase();

        // --- ENFORCEMENT LOGIC ---
        if (!currentSlugInUrl || getStepIndexFromSlug(currentSlugInUrl, steps) === -1 || currentSlugInUrl !== requiredSlug) {
            if (currentSlugInUrl !== requiredSlug) {
                console.warn(`Effect 2: URL step ('${currentSlugInUrl}') doesn't match required ('${requiredSlug}'). Forcing navigation.`);
                setSearchParams({ step: requiredSlug }, { replace: true });
                return;
            }
        }

        // --- SYNC UI ---
        console.log(`Effect 2: URL step ('${currentSlugInUrl}') matches required ('${requiredSlug}'). Syncing UI.`);
        const currentStepIndex = getStepIndexFromSlug(requiredSlug, steps);
        setActiveStep(currentStepIndex);

        setIsProcessingStep(false);
        setIsLoading(false);

    }, [searchParams, setSearchParams, steps, isLoading]);

    // --- Effect 3: Set Document Title ---
    useEffect(() => {
        document.title = 'Account Setup | KlonIT';
    }, []);

    // --- Callbacks & Handlers ---
    const handleStepCompletion = useCallback((nextStepUrl) => {
        setIsProcessingStep(false);
        setError(null);
        console.log("HandleCompletion: Received completion URL:", nextStepUrl);

        const isAdmin = userRole?.toLowerCase() === "admin";
        console.log(nextStepUrl)
        if (nextStepUrl === "/chatbots") {
            localStorage.setItem(SETUP_COMPLETE_KEY, 'true');
            window.location.href = "/chatbots";
        } else {
            const nextSlug = getSlugFromPath(nextStepUrl, steps);
            if (nextSlug) {
                console.log(`HandleCompletion: Mapped URL ${nextStepUrl} to slug ${nextSlug}.`);
                setSearchParams({ step: nextSlug });
            } else {
                console.warn("HandleCompletion: Unknown next_step URL, assuming completion:", nextStepUrl);
                localStorage.setItem(SETUP_COMPLETE_KEY, 'true');
                window.location.href = "/chatbots";
            }
        }
    }, [setSearchParams, steps, userRole]);

    const handleStepError = useCallback((errorMessage) => {
        setIsProcessingStep(false);
        setError(errorMessage || "An error occurred in the current step.");
    }, []);

    const handleNext = async () => {
        setError(null);
        setIsProcessingStep(true);
        const currentStepIndex = activeStep;
        const currentStep = steps[currentStepIndex];
        const isAdmin = userRole?.toLowerCase() === "admin";

        if (currentStep.slug === 'profile') {
            // Profile step
            if (profilePageRef.current?.handleSaveButtonClick) {
                profilePageRef.current.handleSaveButtonClick();
            } else { 
                handleStepError("Could not initiate profile save."); 
            }
        } else if (currentStep.slug === 'terms') {
            // Terms step - handled by TermsAcceptanceStep component itself
            setIsProcessingStep(false);
        } else if (currentStep.slug === 'plan' && isAdmin) {
            // Plan step
            setIsProcessingStep(false);
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            const previousStepSlug = steps[activeStep - 1].slug;
            setSearchParams({ step: previousStepSlug });
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate("/log-out");
    };

    // Pass validity handlers to children
    const handleProfileFormValidity = (isValid) => setProfileFormValid(isValid);

    // Render correct child component based on state
    const getStepContent = (stepIndex) => {
        if (stepIndex < 0 || stepIndex >= steps.length) {
            console.error(`getStepContent called with invalid index: ${stepIndex}`);
            return <Typography color="error">Invalid Step</Typography>;
        }

        const isAdmin = userRole?.toLowerCase() === "admin";
        const currentStep = steps[stepIndex];

        if (currentStep.slug === 'profile') {
            return <ProfilePage
                ref={profilePageRef}
                onFormValidityChange={handleProfileFormValidity}
                onComplete={handleStepCompletion}
                onError={handleStepError}
                includePasswordUpdate={!isAdmin} // Pass prop to show password update for non-admins
                isSetupMode={true}
            />;
        } else if (currentStep.slug === 'terms') {
            // NEW: Terms Acceptance Step
            return <TermsAcceptanceStep
                isAdmin={isAdmin}
                onComplete={handleStepCompletion}
                onError={handleStepError}
                isSetupMode={true}
            />;
        } else if (currentStep.slug === 'plan' && isAdmin) {
            return <PricingPage
                onComplete={handleStepCompletion}
                onError={handleStepError}
                isSetupMode={true}
            />;
        } else {
            return <Typography>Loading Step...</Typography>;
        }
    };

    // --- Render Logic ---
    if (isLoading && !error) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress /> 
                <Typography sx={{ ml: 2 }}>Checking setup status...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '80vh', gap: 2 }}>
                <Typography color="error">Error during setup check: {error}</Typography>
                <Button variant="contained" onClick={handleLogout}>
                    Logout
                </Button>
            </Box>
        );
    }

    const isAdmin = userRole?.toLowerCase() === "admin";
    const setupTitle = isAdmin ? "Account Setup" : "Complete Your Profile";

    // Main Render
    return (
        <Box sx={{ width: '100%' }}>
            <StyledPaper elevation={3}>
                <Typography variant="h5" gutterBottom textAlign="center" color="textPrimary">{setupTitle}</Typography>
                
                {/* Stepper */}
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
                    {steps.map((step) => (
                        <Step key={step.slug}>
                            <StepLabel>{step.label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <InlineError message={error} />

                <Box mt={1}>
                    {getStepContent(activeStep)}
                </Box>

                {/* Navigation Buttons - Only show for non-terms steps */}
                {steps[activeStep]?.slug !== 'terms' && (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'row', 
                        pt: 2, 
                        borderTop: 1, 
                        borderColor: 'divider', 
                        justifyContent: 'space-between', 
                        alignItems: 'center' 
                    }}>
                        <Button 
                            color="inherit" 
                            disabled={activeStep === 0 || isProcessingStep} 
                            onClick={handleBack}
                        >
                            Back
                        </Button>
                        
                        {(activeStep < steps.length - 1) ? (
                            <Button 
                                variant="contained" 
                                onClick={handleNext}
                                disabled={isProcessingStep || (activeStep === 0 && !profileFormValid)}
                            >
                                {isProcessingStep ? <InlineSpinner /> : "Save & Continue"}
                            </Button>
                        ) : (
                            isAdmin && steps[activeStep]?.slug === 'plan' && (
                                <Typography variant="caption" sx={{ fontStyle: 'italic', textAlign: 'right' }}>
                                    Select a plan above to complete setup.
                                </Typography>
                            )
                        )}
                    </Box>
                )}
            </StyledPaper>
        </Box>
    );
}